import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Ticket, Home, Sparkles, Mail, PoundSterling, Flame, Compass, Sprout, Percent, ClipboardList, Wind } from "lucide-react";
import { SiteSettingsCard } from "../components/SiteSettingsCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("feral_admin_token")}` },
});

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

const TABS = [
  { key: "orders", label: "Ticket Sales", icon: Ticket },
  { key: "enquiries", label: "Stay Enquiries", icon: Home },
  { key: "interests", label: "Investment Leads", icon: Sparkles },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "early-access", label: "Fest 2027 Early Access", icon: Flame },
  { key: "unbound", label: "UNBOUND", icon: Compass },
  { key: "waitlist", label: "Waitlist", icon: ClipboardList },
  { key: "breathwork", label: "Meditation Sign-ups", icon: Wind },
  { key: "venue-enquiries", label: "Venue & Discovery Enquiries", icon: ClipboardList },
  { key: "brochure-requests", label: "Brochure Downloads", icon: ClipboardList },
];

const COLUMNS = {
  orders: [
    { k: "created_at", label: "Date" },
    { k: "event_name", label: "Event" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "items", label: "Items" },
    { k: "amount", label: "Total", money: true },
    { k: "payment_plan", label: "Payment", plan: true },
  ],
  enquiries: [
    { k: "created_at", label: "Date" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "venue", label: "Venue" },
    { k: "arrival", label: "Arrival" },
    { k: "guests", label: "Guests" },
    { k: "message", label: "Message" },
  ],
  interests: [
    { k: "created_at", label: "Date" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "amount", label: "Amount" },
    { k: "message", label: "Message" },
  ],
  newsletter: [
    { k: "created_at", label: "Date" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "source", label: "Signed up from" },
  ],
  "early-access": [
    { k: "created_at", label: "Date" },
    { k: "firstName", label: "First" },
    { k: "lastName", label: "Last" },
    { k: "email", label: "Email" },
    { k: "mobile", label: "Mobile" },
    { k: "interests", label: "Interested in" },
    { k: "attended2026", label: "Attended 2026" },
    { k: "message", label: "Message" },
    { k: "source", label: "Source" },
    { k: "utm", label: "UTM" },
  ],
  unbound: [
    { k: "created_at", label: "Date" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "product", label: "Product" },
    { k: "practitioner", label: "Practitioner" },
    { k: "amount", label: "Paid £" },
    { k: "sessions_remaining", label: "Sessions left" },
    { k: "availabilityNote", label: "Availability" },
  ],
  waitlist: [
    { k: "created_at", label: "Date" },
    { k: "event_name", label: "Event" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "message", label: "Why they want to attend" },
  ],
  breathwork: [
    { k: "created_at", label: "Date" },
    { k: "email", label: "Email" },
    { k: "source", label: "Signed up from" },
    { k: "klaviyo_synced", label: "In Klaviyo" },
  ],
  "brochure-requests": [
    { k: "created_at", label: "Date" },
    { k: "email", label: "Email" },
    { k: "klaviyo_synced", label: "In Klaviyo" },
  ],
  "venue-enquiries": [
    { k: "created_at", label: "Date" },
    { k: "kind", label: "Type" },
    { k: "name", label: "Name" },
    { k: "email", label: "Email" },
    { k: "phone", label: "Phone" },
    { k: "company", label: "Company" },
    { k: "group_size", label: "Group size" },
    { k: "dates", label: "Dates" },
    { k: "message", label: "Message" },
  ],
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("orders");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState("all");
  const [allEvents, setAllEvents] = useState([]);
  const [innovPending, setInnovPending] = useState(0);
  useEffect(() => {
    axios.get(`${API}/admin/innovators/summary`, authHeaders()).then((r) => setInnovPending(r.data.pending || 0)).catch(() => {});
  }, []);
  const user = JSON.parse(localStorage.getItem("feral_admin_user") || "{}");

  const logout = useCallback(() => {
    localStorage.removeItem("feral_admin_token");
    localStorage.removeItem("feral_admin_user");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("feral_admin_token")) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/admin/summary`, authHeaders())
      .then((r) => setSummary(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) logout();
      });
    axios
      .get(`${API}/admin/events`, authHeaders())
      .then((r) => setAllEvents(r.data || []))
      .catch(() => {});
  }, [navigate, logout]);

  useEffect(() => {
    if (!localStorage.getItem("feral_admin_token")) return;
    setLoading(true);
    setEventFilter("all");
    axios
      .get(`${API}/admin/${tab}`, authHeaders())
      .then((r) => setRows(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) logout();
      })
      .finally(() => setLoading(false));
  }, [tab, logout]);

  const exportCsv = async () => {
    try {
      const params =
        tab === "orders" && eventFilter !== "all"
          ? { event: eventFilter }
          : {};
      const res = await axios.get(`${API}/admin/export/${tab}`, {
        ...authHeaders(),
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const suffix =
        tab === "orders" && eventFilter !== "all" ? `-${eventFilter}` : "";
      a.download = `feral-${tab}${suffix}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  const cols = COLUMNS[tab];

  // Ticket Sales: per-event breakdown + filtering
  const ticketCount = (itemsStr) =>
    (itemsStr || "")
      .split(";")
      .reduce((sum, part) => {
        const m = part.trim().match(/^(\d+)x/);
        return sum + (m ? parseInt(m[1], 10) : 0);
      }, 0);

  const eventGroups = React.useMemo(() => {
    if (tab !== "orders") return [];
    const map = new Map();
    // Seed with every known event so each shows as its own filter, even with 0 sales.
    allEvents.forEach((ev) => {
      if (ev.slug) {
        map.set(ev.slug, { slug: ev.slug, name: ev.name || ev.slug, orders: 0, tickets: 0, revenue: 0 });
      }
    });
    rows.forEach((r) => {
      const slug = r.event || "__other__";
      const g =
        map.get(slug) || {
          slug,
          name: slug === "__other__" ? "UNBOUND / Other" : r.event_name || slug,
          orders: 0,
          tickets: 0,
          revenue: 0,
        };
      g.orders += 1;
      g.tickets += ticketCount(r.items);
      g.revenue += Number(r.amount) || 0;
      map.set(slug, g);
    });
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue || a.name.localeCompare(b.name));
  }, [rows, tab, allEvents]);

  const visibleRows =
    tab === "orders" && eventFilter !== "all"
      ? rows.filter((r) => (r.event || "__other__") === eventFilter)
      : rows;

  const stats = [
    { label: "Ticket sales", value: summary?.orders ?? "—", icon: Ticket },
    { label: "Revenue", value: summary ? gbp(summary.revenue) : "—", icon: PoundSterling },
    { label: "FERAL POT", value: summary ? gbp(summary.feral_pot) : "—", icon: Sprout, highlight: true },
    { label: "Stay enquiries", value: summary?.enquiries ?? "—", icon: Home },
    { label: "Investment leads", value: summary?.interests ?? "—", icon: Sparkles },
    { label: "Newsletter", value: summary?.newsletter ?? "—", icon: Mail },
    { label: "Fest 2027 Early Access", value: summary?.early_access ?? "—", icon: Flame },
    { label: "UNBOUND", value: summary?.unbound ?? "—", icon: Compass },
    { label: "Waitlist", value: summary?.waitlist ?? "—", icon: ClipboardList },
    { label: "Meditation", value: summary?.breathwork ?? "—", icon: Wind },
  ];

  return (
    <div className="min-h-screen bg-[#f4efe5]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#1c3c38] text-white px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="feral-serif text-2xl">FERAL Admin</h1>
          <p className="text-white/60 text-xs">{user.email}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate("/admin/events")}
            data-testid="admin-events-link"
            className="inline-flex items-center gap-2 bg-[#ce8a39] hover:bg-[#b87a2e] px-4 py-2 rounded-full text-sm font-semibold transition-colors"
          >
            <Ticket size={16} /> Event Manager
          </button>
          <button
            onClick={() => navigate("/admin/innovators")}
            data-testid="admin-innovators-link"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
          >
            <ClipboardList size={16} /> Innovators Invites
            {innovPending > 0 && <span className="bg-[#ce8a39] text-white text-xs rounded-full px-2 py-0.5" data-testid="innovators-pending-count">{innovPending} to review</span>}
          </button>
          <button
            onClick={() => navigate("/admin/discounts")}
            data-testid="admin-discounts-link"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
          >
            <Percent size={16} /> Discounts
          </button>
            <a href="/admin/email-signatures" data-testid="admin-email-signatures-top-link" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-full text-xs font-semibold transition-colors whitespace-nowrap" title="Manage team email signatures">✉ Email signatures</a>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm transition-colors"
          >
            <LogOut size={16} /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <SiteSettingsCard />
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {stats.map((s) => {
            const Ic = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-lg border p-5 ${
                  s.highlight
                    ? "bg-[#1c3c38] border-[#1c3c38] ring-2 ring-[#ce8a39]"
                    : "bg-white border-[#e0d3bf]"
                }`}
              >
                <Ic size={18} className={`mb-2 ${s.highlight ? "text-[#ce8a39]" : "text-[#ce8a39]"}`} />
                <p className={`feral-serif text-2xl leading-none ${s.highlight ? "text-[#ce8a39]" : "text-[#9b3d0a]"}`}>{s.value}</p>
                <p className={`text-xs mt-1.5 ${s.highlight ? "text-[#e2c7b0]" : "text-[#6e5646]"}`}>{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {TABS.map((t) => {
            const Ic = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  active
                    ? "bg-[#9b3d0a] text-white"
                    : "bg-white text-[#6e5646] border border-[#e0d3bf] hover:border-[#9b3d0a]"
                }`}
              >
                <Ic size={15} /> {t.label}
              </button>
            );
          })}
          <button
            onClick={exportCsv}
            data-testid="admin-export-button"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#ce8a39] hover:bg-[#b87a2e] text-white transition-colors"
          >
            <Download size={15} /> Export CSV
          </button>
        </div>

        {/* Ticket Sales — per-event totals & filter */}
        {tab === "orders" && !loading && eventGroups.length > 0 && (
          <div className="mb-5" data-testid="admin-event-breakdown">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
              {eventGroups.map((g) => {
                const active = eventFilter === g.slug;
                return (
                  <button
                    key={g.slug}
                    onClick={() => setEventFilter(active ? "all" : g.slug)}
                    data-testid={`admin-event-card-${g.slug}`}
                    className={`text-left rounded-lg border p-4 transition-all ${
                      active
                        ? "bg-[#1c3c38] border-[#1c3c38] text-white ring-2 ring-[#ce8a39]"
                        : "bg-white border-[#e0d3bf] hover:border-[#9b3d0a]"
                    }`}
                  >
                    <p className={`text-xs font-semibold leading-snug mb-2 truncate ${active ? "text-white/80" : "text-[#6e5646]"}`} title={g.name}>
                      {g.name}
                    </p>
                    <p className={`feral-serif text-2xl leading-none ${active ? "text-[#ce8a39]" : "text-[#9b3d0a]"}`}>
                      {gbp(g.revenue)}
                    </p>
                    <p className={`text-xs mt-1.5 ${active ? "text-white/70" : "text-[#9a876f]"}`}>
                      {g.tickets} ticket{g.tickets === 1 ? "" : "s"} · {g.orders} order{g.orders === 1 ? "" : "s"}
                    </p>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-[#7a6a58] uppercase tracking-wide">Filter:</span>
              <button
                onClick={() => setEventFilter("all")}
                data-testid="admin-event-filter-all"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  eventFilter === "all" ? "bg-[#9b3d0a] text-white" : "bg-white text-[#6e5646] border border-[#e0d3bf] hover:border-[#9b3d0a]"
                }`}
              >
                All events
              </button>
              {eventGroups.map((g) => (
                <button
                  key={g.slug}
                  onClick={() => setEventFilter(g.slug)}
                  data-testid={`admin-event-filter-${g.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    eventFilter === g.slug ? "bg-[#9b3d0a] text-white" : "bg-white text-[#6e5646] border border-[#e0d3bf] hover:border-[#9b3d0a]"
                  }`}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border border-[#e0d3bf] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="bg-[#f3e7d6] text-[#7a6a58] text-left">
                  {cols.map((c) => (
                    <th key={c.k} className="px-4 py-3 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-10 text-center text-[#9a876f]">
                      Loading…
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={cols.length} className="px-4 py-10 text-center text-[#9a876f]" data-testid="admin-empty">
                      Nothing here yet.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((r, i) => (
                    <tr key={i} className="border-t border-[#efe6d7] hover:bg-[#faf6ee]">
                      {cols.map((c) => (
                        <td key={c.k} className="px-4 py-3 text-[#2e322c] align-top max-w-xs">
                          {c.plan ? (
                            r.payment_type === "Plan" ? (
                              <div className="flex flex-col gap-1" data-testid={`admin-plan-${i}`}>
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#9b3d0a]">
                                  Plan · {r.plan_paid ?? 0}/{r.plan_count ?? 4} paid
                                </span>
                                <span className="inline-flex h-1.5 w-24 rounded-full bg-[#e6d8c2] overflow-hidden">
                                  <span
                                    className="h-full bg-[#5b8049]"
                                    style={{ width: `${((r.plan_paid ?? 0) / (r.plan_count || 4)) * 100}%` }}
                                  />
                                </span>
                                {r.plan_status === "payment_failed" ? (
                                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#b3261e] px-2 py-0.5 rounded-full w-fit" data-testid={`admin-plan-failed-${i}`}>
                                    ⚠ Payment failed
                                  </span>
                                ) : r.plan_status === "completed" ? (
                                  <span className="text-[11px] font-semibold text-[#4c6b3d]">Completed</span>
                                ) : (
                                  <span className="text-[11px] text-[#9a876f]">£{r.plan_installment}/mo</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-[#7a6a58]">Paid in full</span>
                            )
                          ) : c.money ? (
                            gbp(r[c.k])
                          ) : (
                            r[c.k] || "—"
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
